import { useEffect, useState } from "react";
import {
  arrangeWidgets,
  breakpoints,
  createLayoutConfiguration,
  getBreakpoint,
  validateLayout,
  widgetIdSplitSymbol,
} from "../gridLayoutConfigurations";
import { Layout } from "react-grid-layout";

interface Params {
  /** Dashboard name */
  dashboard: string;
  /** List of dashboard widgets from data */
  dashboardWidgets: any[];
  /** Staff ID for scoped configuration */
  staffId: number;
}

type LayoutConfig = {
  [breakpoint: string]: Layout[];
};

/**
 * React hook that determines layout configuration for widgets at D-level
 * <br/>
 * ### Logic for grid layout in D-level
 * 1. Get layout configuration from localStorage using staff_id scoping
 * 2. If the layout config does not exist or does not exist for some widgets,
 *    create a new layout based on widget's visual type
 * 3. Validate layout to prevent overlaps and ensure proper positioning
 * 4. Apply widget arrangement algorithm for optimal packing
 */
const useDashboardGridLayout = ({
  dashboard,
  dashboardWidgets,
  staffId = 0,
}: Params) => {
  const [gridLayoutConfiguration, setGridLayoutConfiguration] =
    useState<LayoutConfig | null>(null);

  const getLayoutConfigFromLS = (name: string): LayoutConfig | null => {
    try {
      const res = localStorage.getItem(name);
      if (!res || res === "null") {
        return null;
      }
      return JSON.parse(res);
    } catch (e) {
      console.error(`Error parsing layout configuration for ${name}:`, e);
      return null;
    }
  };

  const saveLayoutToLS = (layout: Layout[]) => {
    try {
      const currentBreakpoint = getBreakpoint(window.innerWidth);
      const storageKey = `staff_${staffId}.layout.dashboard.${dashboard}`;

      // Get existing layout configuration
      let existingLayout: LayoutConfig = {};
      try {
        const stored = localStorage.getItem(storageKey);
        existingLayout = stored ? JSON.parse(stored) : {};
      } catch (e) {
        console.warn("Failed to parse existing layout, creating new one");
        existingLayout = {};
      }

      // Validate layout to prevent overlaps
      const columns = require("../gridLayoutConfigurations").columns;
      const validatedLayout = validateLayout(
        layout,
        columns[currentBreakpoint]
      );

      // Update layout for current breakpoint
      const updatedLayout = {
        ...existingLayout,
        [currentBreakpoint]: validatedLayout,
      };

      localStorage.setItem(storageKey, JSON.stringify(updatedLayout));
    } catch (e) {
      console.error("Failed to save layout to localStorage:", e);
    }
  };

  useEffect(() => {
    if (!dashboard || !dashboardWidgets || dashboardWidgets.length === 0) {
      setGridLayoutConfiguration(null);
      return;
    }

    try {
      const currentBreakpoint = getBreakpoint(window.innerWidth);
      const storageKey = `staff_${staffId}.layout.dashboard.${dashboard}`;

      // Get saved layout configuration
      let savedLayoutConfig = getLayoutConfigFromLS(storageKey);

      // Create widget identifiers
      const widgetIdentifiers = dashboardWidgets.map(
        (d) => `${d.url}${widgetIdSplitSymbol}${d.widget.type}`
      );

      // Create default layout configuration
      const defaultGridLayout = arrangeWidgets(
        createLayoutConfiguration({
          bp: breakpoints,
          items: widgetIdentifiers,
        })
      );

      let finalLayoutConfig = defaultGridLayout;

      if (savedLayoutConfig) {
        try {
          // Check if saved layout matches current widgets
          const savedWidgetIds =
            savedLayoutConfig[currentBreakpoint]?.map((w) => w.i) || [];
          const currentWidgetIds = widgetIdentifiers;

          // If widget sets match, use saved layout, otherwise use default
          if (
            JSON.stringify(savedWidgetIds.sort()) ===
            JSON.stringify(currentWidgetIds.sort())
          ) {
            finalLayoutConfig = savedLayoutConfig;
          } else {
            console.info("Widget configuration changed, using default layout");
          }
        } catch (e) {
          console.warn(
            "Error processing saved layout, falling back to default"
          );
        }
      }

      // Validate final layout to prevent overlaps
      if (finalLayoutConfig[currentBreakpoint]) {
        const columns = require("../gridLayoutConfigurations").columns;
        finalLayoutConfig[currentBreakpoint] = validateLayout(
          finalLayoutConfig[currentBreakpoint],
          columns[currentBreakpoint]
        );
      }

      setGridLayoutConfiguration(finalLayoutConfig);
    } catch (e) {
      console.error("Error initializing grid layout:", e);
      setGridLayoutConfiguration(null);
    }
  }, [staffId, dashboard, JSON.stringify(dashboardWidgets)]);

  const shouldLoadGridLayout = Boolean(
    dashboardWidgets?.length && gridLayoutConfiguration
  );

  return {
    saveLayoutToLS,
    gridLayoutConfiguration,
    shouldLoadGridLayout,
  };
};

export { useDashboardGridLayout };
